import { stripIdleTimeoutParam } from '@/lib/idleTimeoutOverride';

export const SESSION_EXPIRED_STORAGE_KEY = 'clinio_session_expired';
export const SESSION_EXPIRED_SHOWN_KEY = 'clinio_session_expired_shown';
export const SIGN_IN_RETURN_TO_STORAGE_KEY = 'clinio_sign_in_return_to';
export const LAST_SIGNOUT_REASON_KEY = 'clinio_last_signout_reason';
/** Bumped on a successful sign-in attempt. Shared across tabs via localStorage. */
export const AUTH_EPOCH_KEY = 'clinio_auth_epoch';
/** Bumped when a sign-out starts. A later auth epoch wins over this logout. */
export const SIGN_OUT_EPOCH_KEY = 'clinio_signout_epoch';

export type SignOutReason = 'user' | 'inactivity' | 'revoked' | 'expired';

const AUTH_PAGE_PREFIXES = [
  '/sign-in',
  '/sign-up',
  '/create-clinic',
  '/forgot-password',
  '/reset-password',
  '/accept-invitation',
  '/portal/login',
  '/portal/accept-invitation',
  '/portal/reset-password',
] as const;

const PUBLIC_NO_REDIRECT_PREFIXES = [
  '/book/',
  '/pay-invoice/',
  '/accept-quote/',
  '/spoergeskemaer',
  '/legal/',
  '/payment-success',
  '/questionnaire/',
] as const;

export function isAuthPagePath(pathname: string): boolean {
  return AUTH_PAGE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

export function sanitizeReturnTo(path: string | null | undefined): string {
  if (!path || !path.startsWith('/') || path.startsWith('//')) {
    return '/dashboard';
  }
  const stripped = stripIdleTimeoutParam(path);
  const pathname = stripped.split(/[?#]/)[0] || stripped;
  if (isAuthPagePath(pathname)) {
    return '/dashboard';
  }
  return stripped;
}

function readStorageEpoch(key: string): number {
  try {
    const value = Number(localStorage.getItem(key) ?? 0);
    return Number.isFinite(value) && value > 0 ? value : 0;
  } catch {
    return 0;
  }
}

function writeStorageEpoch(key: string, value: number): void {
  try {
    localStorage.setItem(key, String(value));
  } catch {
    // localStorage not available
  }
}

function allocateEpoch(): number {
  return (
    Math.max(Date.now(), readStorageEpoch(AUTH_EPOCH_KEY), readStorageEpoch(SIGN_OUT_EPOCH_KEY)) + 1
  );
}

export function readAuthEpoch(): number {
  return readStorageEpoch(AUTH_EPOCH_KEY);
}

export function readSignOutEpoch(): number {
  return readStorageEpoch(SIGN_OUT_EPOCH_KEY);
}

/** Record that a sign-in is the newest auth event in this browser profile. */
export function bumpAuthEpoch(): number {
  const next = allocateEpoch();
  writeStorageEpoch(AUTH_EPOCH_KEY, next);
  return next;
}

export function restoreAuthEpoch(epoch: number): void {
  if (epoch > 0) writeStorageEpoch(AUTH_EPOCH_KEY, epoch);
  else {
    try {
      localStorage.removeItem(AUTH_EPOCH_KEY);
    } catch {
      // localStorage not available
    }
  }
}

/**
 * Start a logout for the sign-in this tab is actually holding (`sessionEpoch`).
 * Returns null when a newer sign-in already replaced that session — calling
 * GoTrue signOut would delete the new `clinio-v2-auth` entry.
 * `sessionEpoch` 0 means this tab never observed a bump (legacy session).
 */
export function beginSignOutEpoch(sessionEpoch: number): number | null {
  const authEpoch = readAuthEpoch();
  if (sessionEpoch > 0 && authEpoch > sessionEpoch) return null;
  const next = allocateEpoch();
  writeStorageEpoch(SIGN_OUT_EPOCH_KEY, next);
  return next;
}

/**
 * True when a sign-in was recorded after the latest sign-out. A late
 * SIGNED_OUT (this tab's hung GoTrue logout, or a BroadcastChannel message
 * from another tab) must not clear that session. In-memory cycle counters
 * reset on reload; these epochs do not.
 */
export function isSignOutStaleAgainstNewerSignIn(): boolean {
  const authEpoch = readAuthEpoch();
  return authEpoch > 0 && authEpoch > readSignOutEpoch();
}

export function locationToReturnTo(location: {
  pathname: string;
  search?: string;
  hash?: string;
}): string {
  return `${location.pathname}${location.search ?? ''}${location.hash ?? ''}`;
}

export function returnToAsLocation(returnTo: string): {
  pathname: string;
  search: string;
  hash: string;
} {
  const hashIndex = returnTo.indexOf('#');
  const hash = hashIndex >= 0 ? returnTo.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? returnTo.slice(0, hashIndex) : returnTo;
  const searchIndex = withoutHash.indexOf('?');
  const search = searchIndex >= 0 ? withoutHash.slice(searchIndex) : '';
  const pathname = searchIndex >= 0 ? withoutHash.slice(0, searchIndex) : withoutHash;
  return { pathname, search, hash };
}

/**
 * Where to send the user after a staff / unexpected sign-out.
 * Portal stays on the portal login. Public booking / pay pages stay put.
 */
export function resolveSignOutRedirect(pathname: string): string | null {
  if (isAuthPagePath(pathname)) return null;
  if (PUBLIC_NO_REDIRECT_PREFIXES.some((prefix) => pathname.startsWith(prefix))) {
    return null;
  }
  if (pathname.startsWith('/portal')) return '/portal/login';
  return '/sign-in';
}

export function persistLastSignOutReason(reason: SignOutReason): void {
  try {
    localStorage.setItem(LAST_SIGNOUT_REASON_KEY, reason);
  } catch {
    // localStorage not available
  }
}

export function consumeLastSignOutReason(): SignOutReason | null {
  try {
    const value = localStorage.getItem(LAST_SIGNOUT_REASON_KEY);
    if (value === 'user' || value === 'inactivity' || value === 'revoked' || value === 'expired') {
      localStorage.removeItem(LAST_SIGNOUT_REASON_KEY);
      return value;
    }
  } catch {
    // localStorage not available
  }
  return null;
}

export function clearLastSignOutReason(): void {
  try {
    localStorage.removeItem(LAST_SIGNOUT_REASON_KEY);
  } catch {
    // localStorage not available
  }
}

/**
 * True when a newer sign-in completed after this logout started. A late
 * SIGNED_OUT (hung GoTrue logout, other tab) must not wipe that session.
 */
export function isStaleAuthSignOut(authCycle: number, logoutCycle: number | null): boolean {
  return logoutCycle !== null && authCycle !== logoutCycle;
}

export function persistSessionExpiredNotice(reason: SignOutReason): void {
  switch (reason) {
    case 'user':
      return;
    case 'inactivity':
    case 'revoked':
    case 'expired':
      try {
        // One token for every tab in this logout wave. A second persist (other
        // tab timing out, late SIGNED_OUT) must not start a new notice.
        if (localStorage.getItem(SESSION_EXPIRED_STORAGE_KEY)) return;
        const token = String(Date.now());
        localStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, token);
        sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, token);
      } catch {
        try {
          if (!sessionStorage.getItem(SESSION_EXPIRED_STORAGE_KEY)) {
            sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, '1');
          }
        } catch {
          // storage not available
        }
      }
      return;
    default: {
      const _exhaustive: never = reason;
      return _exhaustive;
    }
  }
}

export function consumeSessionExpiredNotice(): boolean {
  try {
    const token =
      sessionStorage.getItem(SESSION_EXPIRED_STORAGE_KEY) ??
      localStorage.getItem(SESSION_EXPIRED_STORAGE_KEY);
    if (!token) return false;
    if (sessionStorage.getItem(SESSION_EXPIRED_SHOWN_KEY) === token) {
      return false;
    }
    sessionStorage.setItem(SESSION_EXPIRED_SHOWN_KEY, token);
    sessionStorage.removeItem(SESSION_EXPIRED_STORAGE_KEY);
    return true;
  } catch {
    // sessionStorage not available
  }
  return false;
}

export function clearSessionExpiredNotice(): void {
  try {
    localStorage.removeItem(SESSION_EXPIRED_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_EXPIRED_STORAGE_KEY);
    sessionStorage.removeItem(SESSION_EXPIRED_SHOWN_KEY);
  } catch {
    // storage not available
  }
}

export function persistSignInReturnTo(path: string): void {
  try {
    sessionStorage.setItem(SIGN_IN_RETURN_TO_STORAGE_KEY, sanitizeReturnTo(path));
  } catch {
    // sessionStorage not available
  }
}

export function consumeSignInReturnTo(): string | null {
  try {
    const stored = sessionStorage.getItem(SIGN_IN_RETURN_TO_STORAGE_KEY);
    if (stored) {
      sessionStorage.removeItem(SIGN_IN_RETURN_TO_STORAGE_KEY);
      return sanitizeReturnTo(stored);
    }
  } catch {
    // sessionStorage not available
  }
  return null;
}
